import type {
  AdminDashboardStatsDto,
  AdminFamilyDetailDto,
  AdminFamilyListItemDto,
  AdminUserDetailDto,
  AdminUserListItemDto,
  ApiSuccess,
  ErrorResponse,
  UserRole,
} from '@familychef/shared';
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';

import { supabase } from './supabase';

// Clerk token injection for Edge Functions (FunctionsClient doesn't use accessToken callback)
let _getClerkToken: (() => Promise<string | null>) | null = null;

export function setFunctionsTokenGetter(getter: () => Promise<string | null>) {
  _getClerkToken = getter;
}

function toQueryString(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) {
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

async function adminInvoke<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    query?: Record<string, string | number | boolean | undefined>;
    body?: Record<string, unknown>;
  } = {},
): Promise<T> {
  // Inject Clerk session token for Edge Functions auth
  if (_getClerkToken) {
    const token = await _getClerkToken();
    if (token) supabase.functions.setAuth(token);
  }

  const { method = 'GET', query, body } = options;
  const fullName = `admin${path}${toQueryString(query)}`;

  const { data, error } = await supabase.functions.invoke(fullName, {
    method,
    body: body ?? undefined,
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const msg = (await error.context.json().catch(() => ({ message: error.message }))) as
        | ErrorResponse
        | { message?: string };
      throw new Error('error' in msg ? msg.error.message : (msg.message ?? error.message));
    }
    if (error instanceof FunctionsRelayError) {
      throw new Error(`Relay error: ${error.message}`);
    }
    if (error instanceof FunctionsFetchError) {
      throw new Error(`Network error: ${error.message}`);
    }
    throw error;
  }

  return (data as ApiSuccess<T>).data;
}

export function fetchAdminDashboardStats() {
  return adminInvoke<AdminDashboardStatsDto>('/stats');
}

export function fetchAdminFamilies(search: string) {
  return adminInvoke<AdminFamilyListItemDto[]>('/families', {
    query: { search: search.trim() || undefined },
  });
}

export function fetchAdminFamilyDetail(id: string) {
  return adminInvoke<AdminFamilyDetailDto>(`/families/${id}`);
}

export function fetchAdminUsers(search: string) {
  return adminInvoke<AdminUserListItemDto[]>('/users', {
    query: { search: search.trim() || undefined },
  });
}

export function fetchAdminUserDetail(id: string) {
  return adminInvoke<AdminUserDetailDto>(`/users/${id}`);
}

export function updateAdminUserRole(id: string, role: UserRole) {
  return adminInvoke<void>(`/users/${id}/role`, {
    method: 'PUT',
    body: { role },
  });
}
